import { supabase } from './supabase';
import { demoAccount, isDemoMode } from './demo/harness';

export type AccountTier = 'free' | 'pro';

export interface Account {
  email: string;
  display_name: string | null;
  tier: AccountTier;
  status: string;
  usage_today: number;
  usage_limit: number;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  has_subscription: boolean;
  // True only when the latest subscription exists in Stripe and is
  // manageable (active/trialing/past_due) — drives the billing CTA so a
  // churned user gets "Upgrade to Pro", not the portal (CC-SPEC item 5).
  // Optional: absent until migration 098 ships; callers fall back to tier.
  has_active_subscription?: boolean;
  // Defense-in-depth connector entitlement returned by get_my_account().
  // Optional while older database migrations remain deployable.
  has_entitlement?: boolean;
  // Web-app route gate (CC-SPEC item 4): true only for the owner while the
  // web client is owner-only. Connector entitlements gate on `tier`, not this.
  app_access: boolean;
}

export type AccountRequestFailure =
  | 'empty'
  | 'unauthorized'
  | 'invalid'
  | 'failed';

export class AccountRequestError extends Error {
  constructor(
    public readonly code: AccountRequestFailure,
    message: string,
    public readonly originalError?: unknown,
  ) {
    super(message);
    this.name = 'AccountRequestError';
  }
}

export type AccountDemoState =
  | 'ready'
  | 'slow'
  | 'empty-once'
  | 'failed-once'
  | 'unauthorized'
  | 'stale-once'
  | 'long'
  | 'free'
  | 'profile-failed-once'
  | 'billing-failed-once';

const ACCOUNT_DEMO_STATES = new Set<AccountDemoState>([
  'ready',
  'slow',
  'empty-once',
  'failed-once',
  'unauthorized',
  'stale-once',
  'long',
  'free',
  'profile-failed-once',
  'billing-failed-once',
]);

/**
 * Development-only route input for route-composed account recovery evidence.
 * The explicit booleans keep the production gate unit-testable.
 */
export function resolveAccountDemoState(
  raw: string | null,
  development = import.meta.env.DEV,
  demoMode = isDemoMode(),
): AccountDemoState | null {
  if (!development || !demoMode || !raw) return null;
  return ACCOUNT_DEMO_STATES.has(raw as AccountDemoState)
    ? (raw as AccountDemoState)
    : null;
}

function currentAccountDemoState(): AccountDemoState | null {
  if (typeof window === 'undefined') return null;
  return resolveAccountDemoState(
    new URLSearchParams(window.location.search).get('accountState'),
  );
}

function takeDemoAttempt(state: AccountDemoState, operation: string): number {
  const key = `prometheus_account_demo_${state}_${operation}`;
  try {
    const current = Number.parseInt(window.sessionStorage.getItem(key) ?? '0', 10);
    window.sessionStorage.setItem(key, String(current + 1));
    return Number.isFinite(current) ? current : 0;
  } catch {
    return 0;
  }
}

function demoAccountForState(
  state: AccountDemoState,
  loadAttempt: number | null,
): Account {
  if (state === 'long') {
    return {
      ...demoAccount,
      email: `${'long-practitioner-account-segment-'.repeat(8)}@example.com`,
      display_name:
        'Alexandra Morgan — International Patent Strategy and Portfolio Operations',
      has_entitlement: true,
    };
  }
  if (state === 'free') {
    return {
      ...demoAccount,
      tier: 'free',
      status: 'none',
      usage_today: 5,
      usage_limit: 5,
      current_period_end: null,
      has_subscription: false,
      has_active_subscription: false,
      has_entitlement: false,
    };
  }
  if (state === 'stale-once' && loadAttempt === 0) {
    return {
      ...demoAccount,
      has_entitlement: false,
    };
  }
  return {
    ...demoAccount,
    has_entitlement: true,
  };
}

export async function getMyAccount(): Promise<Account> {
  if (isDemoMode()) {
    const state = currentAccountDemoState() ?? 'ready';
    const loadAttempt =
      state === 'empty-once'
      || state === 'failed-once'
      || state === 'stale-once'
        ? takeDemoAttempt(state, 'load')
        : null;
    if (state === 'slow') {
      await new Promise((resolve) => setTimeout(resolve, 600));
    }
    if (loadAttempt !== null && loadAttempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
    if (state === 'empty-once' && loadAttempt === 0) {
      throw new AccountRequestError(
        'empty',
        'Your account profile is not ready yet.',
      );
    }
    if (state === 'failed-once' && loadAttempt === 0) {
      throw new AccountRequestError(
        'failed',
        'Account details could not be loaded.',
      );
    }
    if (state === 'unauthorized') {
      throw new AccountRequestError(
        'unauthorized',
        'Your account session is no longer available.',
      );
    }
    return demoAccountForState(state, loadAttempt);
  }
  const { data, error } = await supabase.rpc('get_my_account');
  if (error) {
    throw new AccountRequestError(
      accountErrorLooksUnauthorized(error) ? 'unauthorized' : 'failed',
      accountErrorLooksUnauthorized(error)
        ? 'Your account session is no longer available.'
        : 'Account details could not be loaded.',
      error,
    );
  }
  if (!data) {
    throw new AccountRequestError(
      'empty',
      'Your account profile is not ready yet.',
    );
  }
  return data as Account;
}

export async function updateDisplayName(
  displayName: string,
): Promise<string | null> {
  const normalized = displayName.trim().replace(/\s+/g, ' ');
  if (normalized.length > 120) {
    throw new AccountRequestError(
      'invalid',
      'Display name must be 120 characters or fewer.',
    );
  }
  if (isDemoMode()) {
    const state = currentAccountDemoState();
    if (
      state === 'profile-failed-once'
      && takeDemoAttempt(state, 'profile') === 0
    ) {
      throw new AccountRequestError(
        'failed',
        'Your display name could not be saved.',
      );
    }
    return normalized || null;
  }

  const { error } = await supabase.auth.updateUser({
    data: { display_name: normalized || null },
  });
  if (error) {
    throw new AccountRequestError(
      accountErrorLooksUnauthorized(error) ? 'unauthorized' : 'failed',
      accountErrorLooksUnauthorized(error)
        ? 'Your session changed before the profile could be saved.'
        : 'Your display name could not be saved.',
      error,
    );
  }
  return normalized || null;
}

export function hasStaleEntitlement(account: Account): boolean {
  if (account.has_entitlement === undefined) return false;
  return (
    (account.tier === 'pro' && account.has_entitlement !== true)
    || (account.tier === 'free' && account.has_entitlement === true)
  );
}

export function isCreditsExhaustedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status =
    'status' in error && typeof error.status === 'number'
      ? error.status
      : null;
  const message =
    'message' in error && typeof error.message === 'string'
      ? error.message
      : '';
  return (
    status === 429
    && /\b(?:rate limit exceeded|daily spend cap)\b/i.test(message)
  );
}

const PORTAL_URL = '/api/stripe-portal';

export async function redirectToBillingPortal(accessToken: string, returnUrl?: string): Promise<void> {
  if (isDemoMode()) {
    const state = currentAccountDemoState();
    if (
      state === 'billing-failed-once'
      && takeDemoAttempt(state, 'billing') === 0
    ) {
      throw new AccountRequestError(
        'failed',
        'The billing portal could not be opened.',
      );
    }
    return;
  }
  if (!accessToken.trim()) {
    throw new AccountRequestError(
      'unauthorized',
      'Your session changed before billing could be opened.',
    );
  }
  let response: Response;
  try {
    response = await fetch(PORTAL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(returnUrl ? { return_url: returnUrl } : {}),
    });
  } catch (error) {
    throw new AccountRequestError(
      'failed',
      'The billing portal could not be reached.',
      error,
    );
  }

  if (!response.ok) {
    throw new AccountRequestError(
      response.status === 401 || response.status === 403
        ? 'unauthorized'
        : 'failed',
      response.status === 401 || response.status === 403
        ? 'Your session changed before billing could be opened.'
        : 'The billing portal could not be opened.',
    );
  }

  const body = (await response.json().catch(() => null)) as {
    url?: unknown;
  } | null;
  if (typeof body?.url !== 'string') {
    throw new AccountRequestError(
      'failed',
      'The billing portal returned an invalid destination.',
    );
  }
  let destination: URL;
  try {
    destination = new URL(body.url);
  } catch (error) {
    throw new AccountRequestError(
      'failed',
      'The billing portal returned an invalid destination.',
      error,
    );
  }
  if (destination.protocol !== 'https:') {
    throw new AccountRequestError(
      'failed',
      'The billing portal returned an invalid destination.',
    );
  }
  window.location.href = destination.href;
}

function accountErrorLooksUnauthorized(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const details = error as {
    code?: unknown;
    status?: unknown;
    statusCode?: unknown;
    message?: unknown;
  };
  const status = Number.parseInt(
    String(details.status ?? details.statusCode ?? ''),
    10,
  );
  const code = String(details.code ?? '').toLowerCase();
  const message = String(details.message ?? '').toLowerCase();
  return (
    status === 401
    || status === 403
    || code === '42501'
    || code === '28000'
    || message.includes('not authenticated')
    || message.includes('permission denied')
  );
}
